import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckSquare, Inbox, Loader2, Mail, Square, X } from 'lucide-react'
import {
  getConnectedAccounts, getGoogleConnectUrl,
  importGmailMessages, listGmailMessages,
} from '../../shared/api/gmail'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'

interface Props {
  tripId: string
  onClose: () => void
  onImported: (count: number) => void
}

export function GmailImportPanel({ tripId, onClose, onImported }: Props) {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const { data: accounts = [] } = useQuery({
    queryKey: ['connected-accounts'],
    queryFn: getConnectedAccounts,
  })
  const gmailConnected = accounts.find((a) => a.provider === 'google')?.connected ?? false

  const { data: messages = [], isLoading: loadingMsgs } = useQuery({
    queryKey: ['gmail-messages', tripId],
    queryFn: () => listGmailMessages(tripId),
    enabled: gmailConnected,
  })

  const importMutation = useMutation({
    mutationFn: (ids: string[]) => importGmailMessages(tripId, ids),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['points', tripId] })
      void qc.invalidateQueries({ queryKey: ['links', tripId] })
      onImported(created.length)
      onClose()
    },
  })

  function toggle(id: string) {
    setSelected((prev) => {
      const s = new Set(prev)
      if (s.has(id)) s.delete(id)
      else s.add(id)
      return s
    })
  }

  function toggleAll() {
    setSelected(selected.size === messages.length
      ? new Set()
      : new Set(messages.map((m) => m.message_id)))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="w-full max-w-lg rounded-st-xl border border-st-line bg-st-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-st-line px-5 py-4">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-st-primary" />
            <h2 className="font-semibold text-st-text">Import from Gmail</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-st-sm p-1 text-st-muted hover:bg-st-surface-2">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">
          {!gmailConnected && (
            <div className="rounded-st-lg border border-st-line bg-st-surface-2 p-4 text-center">
              <Mail className="mx-auto mb-2 h-8 w-8 text-st-muted" />
              <p className="text-sm font-medium text-st-text">Gmail not connected</p>
              <p className="mb-3 mt-1 text-xs text-st-muted">
                Connect your Google account to import booking & flight confirmations.
              </p>
              <a href={getGoogleConnectUrl()}>
                <Button variant="accent">Connect Gmail</Button>
              </a>
            </div>
          )}

          {gmailConnected && loadingMsgs && <Spinner label="Searching emails…" />}

          {gmailConnected && !loadingMsgs && messages.length === 0 && (
            <div className="rounded-st-lg border border-dashed border-st-line p-6 text-center">
              <Inbox className="mx-auto mb-2 h-8 w-8 text-st-muted" />
              <p className="text-sm font-medium text-st-text">No travel emails found</p>
              <p className="text-xs text-st-muted">Searched for flight & booking confirmations in your trip date range.</p>
            </div>
          )}

          {gmailConnected && messages.length > 0 && (
            <>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs text-st-muted">{messages.length} emails found</p>
                <button type="button" onClick={toggleAll} className="text-xs text-st-primary hover:underline">
                  {selected.size === messages.length ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div className="max-h-72 space-y-2 overflow-y-auto">
                {messages.map((msg) => (
                  <button
                    type="button"
                    key={msg.message_id}
                    onClick={() => toggle(msg.message_id)}
                    className={`w-full rounded-st-md border p-3 text-left transition ${
                      selected.has(msg.message_id)
                        ? 'border-st-primary bg-st-primary/5'
                        : 'border-st-line bg-st-surface hover:border-st-primary/50'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {selected.has(msg.message_id)
                        ? <CheckSquare className="mt-0.5 h-4 w-4 shrink-0 text-st-primary" />
                        : <Square className="mt-0.5 h-4 w-4 shrink-0 text-st-muted" />}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-st-text">{msg.subject}</p>
                        <p className="text-xs text-st-muted">{msg.from} · {msg.date}</p>
                        <p className="mt-0.5 line-clamp-1 text-xs text-st-muted">{msg.snippet}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {gmailConnected && messages.length > 0 && (
          <div className="flex justify-end gap-2 border-t border-st-line px-5 py-4">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              variant="accent"
              disabled={selected.size === 0 || importMutation.isPending}
              onClick={() => importMutation.mutate([...selected])}
            >
              {importMutation.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Importing…</>
              ) : (
                <>Import {selected.size > 0 ? `${selected.size} email${selected.size > 1 ? 's' : ''}` : 'selected'}</>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
